import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

@Component({
  selector: 'app-apa-ranges',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './apa-ranges.component.html',
  styleUrl: './apa-ranges.component.scss',
})
export class ApaRangesComponent {
  data: any;
  rows: Array<{ label: string; min: number; max: number; value: number; currentValue: number }> = [];
  private allRows: Array<{ label: string; min: number; max: number; value: number; currentValue: number }> = [];
  private filterSub?: Subscription;
  private readonly apaOverridesStorageKey = 'dvm_apa_range_overrides';

  constructor(
    private http: HttpClient,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    const apiUrl = this.data?.apiUrl;
    if (!apiUrl) return;
    this.http.get<any>(apiUrl).subscribe((res) => {
      const normalized = this.extractRows(res).map((row) => ({
        ...row,
        currentValue: this.clampToRange(Number(row.value), Number(row.min), Number(row.max)),
      }));
      this.allRows = this.applySavedOverrides(normalized);
      this.applySelectedRowFilter();
    });

    this.filterSub = this.globalFilterService.filter$.subscribe(() => {
      this.applySelectedRowFilter();
    });
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
  }

  private extractRows(res: any): Array<{ label: string; min: number; max: number; value: number }> {
    if (Array.isArray(res?.responseList?.[0]?.data)) {
      return res.responseList[0].data;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    if (Array.isArray(res)) {
      return res;
    }
    return [];
  }

  getPositionPercent(row: { min: number; max: number; currentValue: number }): number {
    const range = row.max - row.min;
    if (!range) return 0;
    const pct = ((row.currentValue - row.min) / range) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  onSlide(row: { currentValue: number }, event: Event): void {
    const target = event.target as HTMLInputElement;
    row.currentValue = Number(target.value);
    const rowRef = this.allRows.find((r) => this.normalizeLabel(r?.label) === this.normalizeLabel((row as any)?.label));
    if (rowRef) {
      rowRef.currentValue = row.currentValue;
      this.saveOverride(rowRef.label, rowRef.currentValue);
    } else {
      this.saveOverride((row as any)?.label, row.currentValue);
    }
  }

  getStep(row: { min: number; max: number }): number {
    const range = row.max - row.min;
    if (!range || range < 0) return 1;
    return range / 100;
  }

  private clampToRange(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) return min;
    return Math.max(min, Math.min(max, value));
  }

  private applySelectedRowFilter(): void {
    const showSelectedOnly = this.data?.showSelectedProductPriceTypeOnly === true;
    if (!showSelectedOnly) {
      this.rows = [...this.allRows];
      return;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const selectedRaw =
      filters?.requestParams?.['product_price_type'] ??
      filters?.queryParams?.['product_price_type'];
    const selected = Array.isArray(selectedRaw) ? selectedRaw[0] : selectedRaw;
    const normalizedSelected = this.normalizeLabel(selected);

    if (!normalizedSelected) {
      this.rows = [...this.allRows];
      return;
    }

    const filtered = this.allRows.filter((row) => this.normalizeLabel(row?.label) === normalizedSelected);
    this.rows = filtered.length ? filtered : [...this.allRows];
  }

  private normalizeLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private applySavedOverrides(rows: Array<{ label: string; min: number; max: number; value: number; currentValue: number }>) {
    const overrides = this.readOverridesMap();
    if (!overrides) return rows;

    return rows.map((row) => {
      const key = this.normalizeLabel(row?.label);
      const override = overrides[key];
      if (override === undefined || override === null || Number.isNaN(Number(override))) {
        return row;
      }
      const safeValue = this.clampToRange(Number(override), Number(row.min), Number(row.max));
      return { ...row, currentValue: safeValue };
    });
  }

  private saveOverride(label: any, value: number): void {
    const key = this.normalizeLabel(label);
    if (!key) return;
    const map = this.readOverridesMap() || {};
    map[key] = value;
    try {
      sessionStorage.setItem(this.apaOverridesStorageKey, JSON.stringify(map));
    } catch {
      // ignore storage failures
    }
  }

  private readOverridesMap(): Record<string, number> | null {
    try {
      const raw = sessionStorage.getItem(this.apaOverridesStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
}
